import React, { useState, useEffect } from 'react';
import { Edit, X, Calendar, Plus, Save, Download, Eye, MessageSquare } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaHorariosPreadmision() {
    const [asignaturas, setAsignaturas] = useState([]);
    const [horarios, setHorarios] = useState([]); // Changed to array for multiple paralelos
    const [docentes, setDocentes] = useState([]);
    const [salas, setSalas] = useState([]);
    const [gestiones, setGestiones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [editingAsignatura, setEditingAsignatura] = useState(null);
    const [editingHorario, setEditingHorario] = useState(null);  // For editing specific paralelo
    const [selectedGestion, setSelectedGestion] = useState(new Date().getFullYear());
    const [selectedModalidad, setSelectedModalidad] = useState('CPA');
    const [errors, setErrors] = useState({});
    const [showNewAsigModal, setShowNewAsigModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedObservation, setSelectedObservation] = useState(null);
    const [editingSubject, setEditingSubject] = useState(null);
    const [filtroDiaModal, setFiltroDiaModal] = useState('');
    const [filtroDocenteModal, setFiltroDocenteModal] = useState('');
    const [filtroSalaModal, setFiltroSalaModal] = useState('');
    const [newAsigData, setNewAsigData] = useState({
        codigo: '',
        nombre: '',
        tipo: 'Práctica',
        horas: 4
    });

    const initialFormData = {
        id_docente: '',
        capacidad: 30,
        paralelo: '',
        dias: { L: false, M: false, MI: false, J: false, V: false },
        horarios_detalle: {}
    };

    const [formData, setFormData] = useState(initialFormData);

    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const diasCortos = { L: 'Lunes', M: 'Martes', MI: 'Miércoles', J: 'Jueves', V: 'Viernes' };

    // Generar slots de 30 minutos desde 07:00 hasta 22:00
    const generarHoras = () => {
        const slots = [];
        for (let h = 6; h <= 18; h++) {
            slots.push(`${h.toString().padStart(2, '0')}:00`);
            slots.push(`${h.toString().padStart(2, '0')}:30`);
        }
        return slots;
    };
    const horas = generarHoras();

    // Función para obtener el rango del slot (ej: "07:00 - 07:30")
    const getSlotRango = (horaInicio) => {
        const [h, m] = horaInicio.split(':').map(Number);
        let finH = h;
        let finM = m + 30;
        if (finM >= 60) {
            finM = 0;
            finH += 1;
        }
        const horaFin = `${finH.toString().padStart(2, '0')}:${finM.toString().padStart(2, '0')}`;
        return `${horaInicio} - ${horaFin}`;
    };

    useEffect(() => {
        loadData();
    }, [selectedGestion, selectedModalidad]);

    const handleDownloadExcel = () => {
        try {
            if (!horarios || horarios.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            const data = horarios.map((h, idx) => {
                const asigInfo = asignaturas.find(a => a.codigo === (h.asignatura || h.codigo));
                const det = h.horarios_detalle && Array.isArray(h.horarios_detalle) ? h.horarios_detalle : null;

                // Calcular % capacidad
                const cap = parseInt(h.capacidad) || 30;
                const ins = parseInt(h.inscritos) || 0;
                const porc = Math.max(0, Math.round(((cap - ins) / cap) * 100));

                return {
                    'No': idx + 1,
                    'Año': h.gestion,
                    'Código': h.asignatura || h.codigo || '',
                    'Asignatura': asigInfo?.nombre || h.asignatura_nombre || '-',
                    'Docente': getDocenteNombre(h.id_docente),
                    'Sala': det ? det.map(d => d.sala || '-').join(', ') : (h.sala || '-'),
                    'Paralelo': h.paralelo || '-',
                    'Capacidad': h.capacidad || 30,
                    '% Capacidad': `${porc}%`,
                    'Hora Inicio': det ? det.map(d => d.hora_inicio || '-').join(', ') : (h.hora_inicio?.substring(0, 5) || '-'),
                    'Hora Fin': det ? det.map(d => d.hora_fin || '-').join(', ') : (h.hora_fin?.substring(0, 5) || '-'),
                    'Día': det ? det.map(d => getDiasAbrev(d.dia)).join(', ') : getDiasAbrev(h.dia_semana),
                    'Estado': h.estado_direccion || 'borrador',
                    'Observaciones': h.observaciones_direccion || '',
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Horarios');
            XLSX.writeFile(wb, `Horarios_Preadmision_${selectedGestion}_${selectedModalidad}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };
    const loadData = async () => {
        setLoading(true);
        try {
            const [horariosRes, docentesRes, asignaturasRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/horarios-preadmision?gestion=${selectedGestion}`),
                fetch(`${API_URL}/jefatura/docentes`),
                fetch(`${API_URL}/jefatura/asignaturas-preadmision?modalidad=${selectedModalidad}`)
            ]);

            const horariosData = await horariosRes.json();
            const docentesData = await docentesRes.json();
            const asignaturasData = await asignaturasRes.json();

            // Store horarios as array, filtered by modalidad
            const filteredHorarios = (horariosData.horarios || []).filter(h => {
                const codigo = h.asignatura || h.codigo || '';
                return codigo.startsWith(selectedModalidad);
            });

            setHorarios(filteredHorarios);
            setDocentes(docentesData.docentes || []);
            setAsignaturas(asignaturasData.asignaturas || []);

            // Gestiones
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let y = 2020; y <= currentYear + 1; y++) {
                years.push(y);
            }
            setGestiones(years);

            // Salas
            const salasArr = [];
            for (let i = 1; i <= 20; i++) {
                salasArr.push(i.toString());
            }
            setSalas(salasArr);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('dia_')) {
            const dia = name.replace('dia_', '');
            const diaFull = diasCortos[dia];
            setFormData(prev => {
                const newDias = { ...prev.dias, [dia]: checked };
                const newDetalle = { ...prev.horarios_detalle };
                if (checked) {
                    newDetalle[diaFull] = newDetalle[diaFull] || { hora_inicio: '', hora_fin: '', sala: '' };
                } else {
                    delete newDetalle[diaFull];
                }
                return { ...prev, dias: newDias, horarios_detalle: newDetalle };
            });
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleDetalleChange = (diaFull, field, value) => {
        setFormData(prev => ({
            ...prev,
            horarios_detalle: {
                ...prev.horarios_detalle,
                [diaFull]: {
                    ...prev.horarios_detalle[diaFull],
                    [field]: value
                }
            }
        }));
    };

    // Obtener TODOS los horarios/paralelos para una asignatura
    const getHorarios = (codigo) => {
        return horarios.filter(h => (h.asignatura || h.codigo) === codigo);
    };

    // Obtener primer horario (para compatibilidad)
    const getHorario = (codigo) => {
        return horarios.find(h => (h.asignatura || h.codigo) === codigo);
    };

    // Agregar nuevo paralelo (form vacío)
    const handleAddParalelo = (asignatura) => {
        setFormData({
            id_docente: '',
            capacidad: 30,
            paralelo: '',
            dias: { L: false, M: false, MI: false, J: false, V: false },
            horarios_detalle: {}
        });
        setEditingAsignatura(asignatura);
        setEditingHorario(null);
        setErrors({});
        setShowModal(true);
    };

    // Editar paralelo existente
    const handleEditParalelo = (asignatura, horario) => {
        const diasObj = { L: false, M: false, MI: false, J: false, V: false };
        if (horario.dia_semana) {
            const diasArray = horario.dia_semana.split(',');
            diasArray.forEach(dia => {
                const diaLower = dia.trim().toLowerCase();
                if (diaLower === 'lunes') diasObj.L = true;
                if (diaLower === 'martes') diasObj.M = true;
                if (diaLower === 'miércoles' || diaLower === 'miercoles') diasObj.MI = true;
                if (diaLower === 'jueves') diasObj.J = true;
                if (diaLower === 'viernes') diasObj.V = true;
            });
        }

        // Cargar horarios_detalle si existe, sino migrar
        let horariosDetalle = {};
        if (horario.horarios_detalle && Array.isArray(horario.horarios_detalle) && horario.horarios_detalle.length > 0) {
            horario.horarios_detalle.forEach(d => {
                horariosDetalle[d.dia] = {
                    hora_inicio: d.hora_inicio || '',
                    hora_fin: d.hora_fin || '',
                    sala: d.sala || ''
                };
            });
        } else {
            const horaInicio = horario.hora_inicio?.substring(0, 5) || '';
            const horaFin = horario.hora_fin?.substring(0, 5) || '';
            const diasGuardados = horario.dia_semana ? horario.dia_semana.split(',') : [];
            diasGuardados.forEach(dia => {
                horariosDetalle[dia.trim()] = {
                    hora_inicio: horaInicio,
                    hora_fin: horaFin,
                    sala: horario.sala || ''
                };
            });
        }

        setFormData({
            id_docente: horario.id_docente || '',
            capacidad: horario.capacidad || 30,
            paralelo: horario.paralelo || '',
            dias: diasObj,
            horarios_detalle: horariosDetalle
        });
        setEditingAsignatura(asignatura);
        setEditingHorario(horario);
        setErrors({});
        setShowModal(true);
    };

    // Keep old handleEdit for backwards compat - now adds new paralelo
    const handleEdit = (asignatura) => handleAddParalelo(asignatura);

    const handleSubmit = async () => {
        // Validar
        const newErrors = {};
        if (!formData.id_docente) newErrors.id_docente = 'Requerido';
        const diasSeleccionados = Object.values(formData.dias).some(v => v);
        if (!diasSeleccionados) newErrors.dias = 'Seleccione al menos un día';
        // Validar cada día tiene hora y sala
        if (diasSeleccionados) {
            Object.entries(formData.dias).forEach(([key, active]) => {
                if (active) {
                    const diaFull = diasCortos[key];
                    const detalle = formData.horarios_detalle[diaFull];
                    if (!detalle?.hora_inicio) newErrors[`detalle_${diaFull}_hora_inicio`] = 'Requerido';
                    if (!detalle?.hora_fin) newErrors[`detalle_${diaFull}_hora_fin`] = 'Requerido';
                    if (!detalle?.sala) newErrors[`detalle_${diaFull}_sala`] = 'Requerido';
                }
            });
        }

        if (formData.paralelo && (formData.paralelo.length < 1 || formData.paralelo.length > 2)) {
            newErrors.paralelo = 'Máximo 2 caracteres';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            const diasActivos = Object.entries(formData.dias).filter(([_, v]) => v).map(([d]) => diasCortos[d]);
            const diasString = diasActivos.join(',');

            const horariosDetalle = Object.entries(formData.horarios_detalle)
                .filter(([dia]) => diasActivos.includes(dia))
                .map(([dia, detalle]) => ({
                    dia,
                    hora_inicio: detalle.hora_inicio,
                    hora_fin: detalle.hora_fin,
                    sala: detalle.sala
                }));

            const payload = {
                gestion: selectedGestion,
                asignatura: editingAsignatura.codigo,
                sala: horariosDetalle[0]?.sala || '',
                id_docente: formData.id_docente,
                capacidad: formData.capacidad || 30,
                hora_inicio: horariosDetalle[0]?.hora_inicio || '',
                hora_fin: horariosDetalle[0]?.hora_fin || '',
                paralelo: formData.paralelo || null,
                dia_semana: diasString,
                horarios_detalle: horariosDetalle
            };

            let response;

            if (editingHorario?.id_horario_preadmision) {
                // Actualizar
                response = await fetch(`${API_URL}/jefatura/horarios-preadmision/${editingHorario.id_horario_preadmision}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                // Crear nuevo
                response = await fetch(`${API_URL}/jefatura/horarios-preadmision`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();
            if (!response.ok) {
                alert(data.error || 'Error al guardar');
                return;
            }

            if (data.warning) {
                alert(data.warning);
            }

            setShowModal(false);
            setFormData(initialFormData);
            setEditingAsignatura(null);
            loadData();
        } catch (error) {
            console.error('Error:', error);
            alert('Error al procesar la solicitud');
        }
    };

    const getDocenteNombre = (id) => {
        const docente = docentes.find(d => d.id_docente == id);
        return docente ? `${docente.nombres} ${docente.apellidos}` : '-';
    };

    const getDiasAbrev = (diasStr) => {
        if (!diasStr) return '-';
        return diasStr.split(',').map(d => {
            const dia = d.trim().toLowerCase();
            if (dia === 'lunes') return 'L';
            if (dia === 'martes') return 'M';
            if (dia === 'miércoles' || dia === 'miercoles') return 'MI';
            if (dia === 'jueves') return 'J';
            if (dia === 'viernes') return 'V';
            return d;
        }).join(', ');
    };

    // Abrir modal para crear nueva asignatura
    const openNewAsigModal = () => {
        setEditingSubject(null);
        setNewAsigData({
            codigo: `${selectedModalidad}-${String(asignaturas.length + 1).padStart(2, '0')}`,
            nombre: '',
            tipo: 'Práctica',
            horas: 4
        });
        setShowNewAsigModal(true);
    };

    // Abrir modal para editar asignatura existente
    const openEditSubjectModal = (asig) => {
        setEditingSubject(asig);
        setNewAsigData({
            codigo: asig.codigo || '',
            nombre: asig.nombre || '',
            tipo: asig.tipo || 'Práctica',
            horas: asig.horas || 4
        });
        setShowNewAsigModal(true);
    };

    // Crear o editar asignatura
    const handleSaveAsignatura = async () => {
        if (!newAsigData.codigo || !newAsigData.nombre) {
            alert('Código y nombre son requeridos');
            return;
        }

        try {
            let response;
            if (editingSubject) {
                // Editar
                response = await fetch(`${API_URL}/jefatura/asignaturas-preadmision/${editingSubject.id_asignatura_preadmision}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...newAsigData,
                        modalidad: selectedModalidad
                    })
                });
            } else {
                // Crear
                response = await fetch(`${API_URL}/jefatura/asignaturas-preadmision`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...newAsigData,
                        modalidad: selectedModalidad
                    })
                });
            }

            if (response.ok) {
                alert(editingSubject ? 'Asignatura actualizada' : 'Asignatura creada exitosamente');
                setShowNewAsigModal(false);
                setEditingSubject(null);
                loadData();
            } else {
                const error = await response.json();
                alert('Error: ' + (error.message || 'Error al guardar'));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al procesar la solicitud');
        }
    };

    if (loading) {
        return <div className="p-6 text-center">Cargando...</div>;
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">
                    Jefatura de Carrera &gt; Asignaturas &gt; {selectedModalidad === 'CPA' ? 'Admisión' : 'Prueba Admisión'}
                </h1>
            </div>

            {/* Tabs CPA / PAA */}
            <div className="mb-4 flex gap-2">
                <button
                    onClick={() => setSelectedModalidad('CPA')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${selectedModalidad === 'CPA'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    Curso de Preadmisión
                </button>
                <button
                    onClick={() => setSelectedModalidad('PAA')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${selectedModalidad === 'PAA'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    Prueba de Admisión Académica
                </button>
            </div>

            {/* Toolbar */}
            <div className="mb-4 flex flex-wrap gap-3 items-center">
                <div className="relative">
                    <select
                        value={selectedGestion}
                        onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                        className="px-6 py-2 pr-10 border border-gray-300 rounded-full bg-white text-sm appearance-none"
                    >
                        {gestiones.map(g => (
                            <option key={g} value={g}>Gestión {g}</option>
                        ))}
                    </select>
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400 text-xs">▼</span>
                </div>

                <button
                    onClick={() => setShowCalendarModal(true)}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                >
                    <Calendar size={18} />
                    Ver Horario
                </button>

                <button
                    onClick={openNewAsigModal}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700"
                >
                    <Plus size={18} />
                    Añadir Asignatura
                </button>

                <button
                    onClick={() => setShowApprovalModal(true)}
                    className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600"
                >
                    Solicitar aprobación
                </button>
                <button
                    onClick={handleDownloadExcel}
                    className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                    title="Descargar Excel"
                >
                    <Download size={20} />
                </button>
            </div>

            {/* Tabla de Asignaturas - SIEMPRE muestra las 4 materias */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Día</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Inicio</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Fin</th>
                                <th className="px-3 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">% Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {asignaturas.map((asig, index) => {
                                const paralelos = getHorarios(asig.codigo);

                                // Si no tiene paralelos, mostrar fila con datos vacíos
                                if (paralelos.length === 0) {
                                    return (
                                        <tr key={asig.id_asignatura_preadmision || asig.codigo} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-center border">{index + 1}</td>
                                            <td className="px-3 py-2 text-center border font-medium">{asig.codigo}</td>
                                            <td className="px-3 py-2 border">
                                                <div className="text-gray-600">{asig.nombre}</div>
                                            </td>
                                            <td className="px-3 py-2 text-center border" colSpan={10}>
                                                <span className="text-gray-400 italic text-xs">Sin paralelos asignados</span>
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                <div className="flex justify-center gap-1">
                                                    <button
                                                        onClick={() => openEditSubjectModal(asig)}
                                                        className="p-1.5 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                                        title="Editar Asignatura"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleAddParalelo(asig)}
                                                        className="p-1.5 rounded-full transition shadow-sm bg-green-100 text-green-600 hover:bg-green-200"
                                                        title="Agregar paralelo"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                // Si tiene paralelos, mostrar una fila por cada paralelo
                                return paralelos.map((horario, pIdx) => (
                                    <tr key={`${asig.codigo}-${horario.id_horario_preadmision || pIdx}`} className={`hover:bg-gray-50 ${pIdx > 0 ? 'bg-gray-50/50' : ''}`}>
                                        {/* Solo mostrar número y código en la primera fila del grupo */}
                                        {pIdx === 0 ? (
                                            <>
                                                <td className="px-3 py-2 text-center border" rowSpan={paralelos.length}>{index + 1}</td>
                                                <td className="px-3 py-2 text-center border font-medium" rowSpan={paralelos.length}>{asig.codigo}</td>
                                                <td className="px-3 py-2 border" rowSpan={paralelos.length}>
                                                    <div className="text-gray-600">{asig.nombre}</div>
                                                </td>
                                            </>
                                        ) : null}
                                        <td className="px-3 py-2 text-center border">
                                            {horario.id_docente ? getDocenteNombre(horario.id_docente) : <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const det = horario.horarios_detalle && Array.isArray(horario.horarios_detalle) ? horario.horarios_detalle : null;
                                                return det ? det.map((d, i) => <div key={i}>{getDiasAbrev(d.dia)}</div>) : getDiasAbrev(horario.dia_semana);
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const det = horario.horarios_detalle && Array.isArray(horario.horarios_detalle) ? horario.horarios_detalle : null;
                                                return det ? det.map((d, i) => <div key={i}>{d.hora_inicio || '-'}</div>) : (horario.hora_inicio?.substring(0, 5) || '-');
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const det = horario.horarios_detalle && Array.isArray(horario.horarios_detalle) ? horario.horarios_detalle : null;
                                                return det ? det.map((d, i) => <div key={i}>{d.hora_fin || '-'}</div>) : (horario.hora_fin?.substring(0, 5) || '-');
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">{horario.capacidad || 30}</td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const cap = parseInt(horario.capacidad) || 30;
                                                const ins = parseInt(horario.inscritos) || 0;
                                                const porc = Math.min(100, Math.round((ins / cap) * 100));
                                                return (
                                                    <div className="flex flex-col items-center">
                                                        <span className={`font-bold ${porc >= 100 ? 'text-red-600' : porc > 80 ? 'text-orange-600' : 'text-green-600'}`}>
                                                            {ins}/{cap} ({porc}%)
                                                        </span>
                                                        {porc >= 100 && <span className="text-[10px] text-red-700 font-bold uppercase">Lleno</span>}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const det = horario.horarios_detalle && Array.isArray(horario.horarios_detalle) ? horario.horarios_detalle : null;
                                                return det ? det.map((d, i) => <div key={i}>{d.sala || '-'}</div>) : (horario.sala || '-');
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">{horario.paralelo || '-'}</td>
                                        <td className="px-3 py-2 text-center border">
                                            {(() => {
                                                const estado = horario.estado_direccion || 'borrador';
                                                const estilos = {
                                                    borrador: 'bg-gray-100 text-gray-600',
                                                    pendiente: 'bg-yellow-100 text-yellow-700',
                                                    aprobado: 'bg-green-100 text-green-700',
                                                    rechazado: 'bg-red-100 text-red-700'
                                                };
                                                const labels = {
                                                    borrador: 'Sin enviar',
                                                    pendiente: 'Pendiente',
                                                    aprobado: 'Aprobado',
                                                    rechazado: 'Rechazado'
                                                };
                                                return (
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${estilos[estado] || estilos.borrador}`}>
                                                        {labels[estado] || labels.borrador}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            {horario.observaciones_direccion ? (
                                                <button
                                                    onClick={() => setSelectedObservation(horario.observaciones_direccion)}
                                                    className="p-1.5 rounded-full transition shadow-sm bg-orange-100 text-orange-600 hover:bg-orange-200"
                                                    title="Ver observaciones"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            ) : (
                                                <span className="text-gray-300 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center border">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => handleEditParalelo(asig, horario)}
                                                    className="p-1.5 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200"
                                                    title="Editar paralelo"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                {pIdx === 0 && (
                                                    <>
                                                        <button
                                                            onClick={() => openEditSubjectModal(asig)}
                                                            className="p-1.5 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                                            title="Editar asignatura"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleAddParalelo(asig)}
                                                            className="p-1.5 rounded-full transition shadow-sm bg-green-100 text-green-600 hover:bg-green-200"
                                                            title="Agregar otro paralelo"
                                                        >
                                                            <Plus size={14} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Editar - Asignar Horario y Docente */}
            {showModal && editingAsignatura && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">
                                    {editingHorario ? 'Editar Paralelo' : 'Agregar Paralelo'}
                                </h2>
                                <p className="text-sm text-gray-600">{editingAsignatura.codigo} - {editingAsignatura.nombre}</p>
                            </div>
                            <button onClick={() => { setShowModal(false); setEditingHorario(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <X size={28} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 max-h-[65vh] scrollbar-thin">
                            {/* Fila 1: Docente, Capacidad, Paralelo */}
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Docente *</label>
                                    <select name="id_docente" value={formData.id_docente} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.id_docente ? 'border-red-500' : 'border-gray-300'}`}>
                                        <option value="">Seleccionar...</option>
                                        {docentes.map(d => <option key={d.id_docente} value={d.id_docente}>{d.nombres} {d.apellidos}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Capacidad</label>
                                    <input type="number" name="capacidad" value={formData.capacidad} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Paralelo</label>
                                    <input
                                        type="text"
                                        name="paralelo"
                                        value={formData.paralelo || ''}
                                        onChange={(e) => setFormData({ ...formData, paralelo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2) })}
                                        placeholder="Ej: A, A1"
                                        maxLength={2}
                                        className={`w-full px-3 py-2 border-2 rounded-md font-mono text-center text-lg ${errors.paralelo ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.paralelo && <p className="text-red-500 text-xs mt-1">{errors.paralelo}</p>}
                                </div>
                            </div>

                            {/* Días con horarios por día */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-2">Días y Horarios por Día *</label>
                                <div className="space-y-3">
                                    {Object.entries(diasCortos).map(([key, diaFull]) => {
                                        const isActive = formData.dias[key];
                                        const detalle = formData.horarios_detalle[diaFull] || {};
                                        return (
                                            <div key={key} className={`border rounded-lg p-3 ${isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name={`dia_${key}`}
                                                        checked={isActive}
                                                        onChange={handleInputChange}
                                                        className="w-5 h-5 rounded border-gray-300 text-blue-600"
                                                    />
                                                    <span className="font-medium text-sm">{diaFull}</span>
                                                </label>
                                                {isActive && (
                                                    <div className="grid grid-cols-3 gap-3 mt-3 pl-8">
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Hora Inicio</label>
                                                            <input type="time" value={detalle.hora_inicio || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'hora_inicio', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />

                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Hora Fin</label>
                                                            <input
                                                                type="time"
                                                                value={detalle.hora_fin || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'hora_fin', e.target.value)}
                                                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>


                                                        <div>
                                                            <label className="block text-xs font-medium mb-1 text-gray-600">Sala</label>
                                                            <select
                                                                value={detalle.sala || ''}
                                                                onChange={(e) => handleDetalleChange(diaFull, 'sala', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm appearance-none"
                                                            >
                                                                <option value="">Sala...</option>
                                                                {detalle.sala && !salas.includes(detalle.sala) && (
                                                                    <option value={detalle.sala}>{detalle.sala}</option>
                                                                )}
                                                                {salas.map(s => <option key={s} value={s}>{s}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {errors.dias && <p className="text-red-500 text-xs mt-1">{errors.dias}</p>}
                            </div>

                            {/* Botón Guardar */}
                            <div className="flex justify-end">
                                <button onClick={handleSubmit}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Calendario */}
            {showCalendarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 flex-wrap gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">
                                    {selectedModalidad === 'CPA' ? 'Curso Preadmisión' : 'Prueba Admisión'} - Gestión {selectedGestion}
                                </h2>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Filtro por Día */}
                                <select
                                    value={filtroDiaModal}
                                    onChange={(e) => setFiltroDiaModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Días</option>
                                    {diasSemana.map(dia => (
                                        <option key={dia} value={dia}>{dia}</option>
                                    ))}
                                    <option value="Sábado">Sábado</option>
                                </select>

                                {/* Filtro por Docente */}
                                <select
                                    value={filtroDocenteModal}
                                    onChange={(e) => setFiltroDocenteModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500 max-w-[200px]"
                                >
                                    <option value="">Todos los Docentes</option>
                                    {docentes.map(doc => (
                                        <option key={doc.id_docente} value={doc.id_docente}>
                                            {doc.nombres} {doc.apellidos}
                                        </option>
                                    ))}
                                </select>

                                {/* Filtro por Sala */}
                                <select
                                    value={filtroSalaModal}
                                    onChange={(e) => setFiltroSalaModal(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las Salas</option>
                                    {salas.map(s => (
                                        <option key={s} value={s}>{`Sala ${s}`}</option>
                                    ))}
                                </select>

                                <button
                                    onClick={() => {
                                        const horariosFiltradosModal = horarios.filter(h => {
                                            if (filtroDocenteModal && String(h.id_docente) !== String(filtroDocenteModal)) return false;
                                            if (filtroDiaModal) {
                                                if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
                                                    const tieneDia = h.horarios_detalle.some(d => d.dia === filtroDiaModal);
                                                    if (!tieneDia) return false;
                                                } else {
                                                    if (!h.dia_semana || !h.dia_semana.includes(filtroDiaModal)) return false;
                                                }
                                            }
                                            if (filtroSalaModal) {
                                                if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
                                                    if (filtroDiaModal) {
                                                        const cumpleAmbos = h.horarios_detalle.some(d => d.dia === filtroDiaModal && d.sala?.toString() === filtroSalaModal.toString());
                                                        if (!cumpleAmbos) return false;
                                                    } else {
                                                        const tieneSala = h.horarios_detalle.some(d => d.sala?.toString() === filtroSalaModal.toString());
                                                        if (!tieneSala) return false;
                                                    }
                                                } else {
                                                    if (h.sala?.toString() !== filtroSalaModal.toString()) return false;
                                                }
                                            }
                                            return true;
                                        });

                                        const data = horariosFiltradosModal.map((h, idx) => {
                                            const det = h.horarios_detalle && Array.isArray(h.horarios_detalle) ? h.horarios_detalle : null;
                                            const asigInfo = asignaturas.find(a => a.codigo === (h.asignatura || h.codigo));

                                            return {
                                                'No': idx + 1,
                                                'Código': h.asignatura || h.codigo || '-',
                                                'Asignatura': asigInfo?.nombre || '-',
                                                'Sala': det ? det.map(d => d.sala || '-').join(', ') : (h.sala || '-'),
                                                'Paralelo': h.paralelo || '-',
                                                'Docente': getDocenteNombre(h.id_docente),
                                                'Hora Inicio': det ? det.map(d => d.hora_inicio || '-').join('\n') : (h.hora_inicio || '-'),
                                                'Hora Fin': det ? det.map(d => d.hora_fin || '-').join('\n') : (h.hora_fin || '-'),
                                                'Días': det ? det.map(d => getDiasAbrev(d.dia)).join(', ') : getDiasAbrev(h.dia_semana),
                                                'Capacidad': h.capacidad || 30
                                            };
                                        });

                                        const ws = XLSX.utils.json_to_sheet(data);
                                        const wb = XLSX.utils.book_new();
                                        XLSX.utils.book_append_sheet(wb, ws, 'Horario');
                                        XLSX.writeFile(wb, `Horario_${selectedModalidad}_${selectedGestion}.xlsx`);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium transition shadow-sm"
                                    title="Exportar a Excel"
                                >
                                    <Download size={16} />
                                </button>
                                <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow overflow-auto p-4 bg-gray-100">
                            {(() => {
                                const rawHorarios = horarios.filter(h => {
                                    const estado = h.estado_direccion || 'borrador';
                                    if (estado === 'rechazado' || estado === 'pendiente') return false;
                                    if (filtroDocenteModal && String(h.id_docente) !== String(filtroDocenteModal)) return false;
                                    if (filtroSalaModal) {
                                        if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
                                            const tieneSala = h.horarios_detalle.some(d => d.sala?.toString() === filtroSalaModal.toString());
                                            if (!tieneSala) return false;
                                        } else {
                                            if (h.sala?.toString() !== filtroSalaModal.toString()) return false;
                                        }
                                    }
                                    return true;
                                });

                                // Construir entradas normalizadas compatibles con la estructura basada en franjas y carriles (lanes)
                                const horariosProcesados = [];
                                rawHorarios.forEach(h => {
                                    const asigInfo = asignaturas.find(a => a.codigo === (h.asignatura || h.codigo));
                                    const nombreAsig = asigInfo?.nombre || h.nombre_asignatura || h.asignatura || '-';
                                    const codigoAsig = h.asignatura || h.codigo || '-';
                                    const docenteNom = getDocenteNombre(h.id_docente);

                                    if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
                                        h.horarios_detalle.forEach(det => {
                                            if (filtroDiaModal && det.dia !== filtroDiaModal) return;
                                            if (filtroSalaModal && det.sala?.toString() !== filtroSalaModal.toString()) return;
                                            horariosProcesados.push({
                                                ...h,
                                                codigo: codigoAsig,
                                                asignatura_nombre: nombreAsig,
                                                sala: det.sala,
                                                dia: det.dia,
                                                hora_inicio: det.hora_inicio,
                                                hora_fin: det.hora_fin,
                                                docente_nombre: docenteNom
                                            });
                                        });
                                    } else {
                                        const diaH = h.dia_semana || h.dia || '';
                                        if (filtroDiaModal && !diaH.includes(filtroDiaModal)) return;
                                        horariosProcesados.push({
                                            ...h,
                                            codigo: codigoAsig,
                                            asignatura_nombre: nombreAsig,
                                            dia: diaH,
                                            docente_nombre: docenteNom
                                        });
                                    }
                                });

                                const diasTabla = diasSemana.map(d => d.toLowerCase());
                                const slotMinutes = 30;
                                const startHour = 6, endHour = 18;
                                const totalSlots = ((endHour - startHour) * 60) / slotMinutes;

                                const toMin = (hhmm) => {
                                    if (!hhmm) return 0;
                                    const partes = hhmm.substring(0, 5).split(':').map(Number);
                                    let h = partes[0] || 0;
                                    const m = partes[1] || 0;
                                    return h * 60 + m;
                                };

                                const dayData = diasSemana.map(diaOriginal => {
                                    const diaKey = diaOriginal.toLowerCase();
                                    const entries = horariosProcesados
                                        .filter(h => {
                                            const dStr = (h.dia || '').toLowerCase();
                                            return dStr.includes(diaKey) && h.hora_inicio && h.hora_fin;
                                        })
                                        .map(h => ({
                                            ...h,
                                            startMin: toMin(h.hora_inicio),
                                            endMin: toMin(h.hora_fin),
                                        }))
                                        .filter(h => h.startMin >= startHour * 60 && h.endMin <= endHour * 60)
                                        .sort((a, b) => a.startMin - b.startMin);

                                    const laneEnds = [];
                                    entries.forEach(e => {
                                        let laneIdx = laneEnds.findIndex(end => end <= e.startMin);
                                        if (laneIdx === -1) {
                                            laneIdx = laneEnds.length;
                                            laneEnds.push(e.endMin);
                                        } else {
                                            laneEnds[laneIdx] = e.endMin;
                                        }
                                        e.lane = laneIdx;
                                    });

                                    const maxLanes = Math.max(1, laneEnds.length);
                                    return { dia: diaOriginal, diaKey, entries, maxLanes };
                                });

                                const totalLanes = dayData.reduce((sum, d) => sum + d.maxLanes, 0);
                                let acumulado = 0;
                                const dayOffsets = dayData.map(d => {
                                    const offset = acumulado;
                                    acumulado += d.maxLanes;
                                    return offset;
                                });

                                const paletaColores = [
                                    'bg-blue-600 hover:bg-blue-700',
                                    'bg-emerald-600 hover:bg-emerald-700',
                                    'bg-amber-600 hover:bg-amber-700',
                                    'bg-rose-600 hover:bg-rose-700',
                                    'bg-indigo-600 hover:bg-indigo-700',
                                    'bg-orange-600 hover:bg-orange-700',
                                    'bg-teal-600 hover:bg-teal-700',
                                    'bg-purple-600 hover:bg-purple-700',
                                    'bg-cyan-600 hover:bg-cyan-700',
                                    'bg-pink-600 hover:bg-pink-700'
                                ];

                                const obtenerColorDistintivo = (identificador, indice) => {
                                    let hash = 0;
                                    const str = String(identificador || indice);
                                    for (let i = 0; i < str.length; i++) {
                                        hash = str.charCodeAt(i) + ((hash << 5) - hash);
                                    }
                                    return paletaColores[Math.abs(hash) % paletaColores.length];
                                };

                                const subjectColorMap = {};
                                horariosProcesados.forEach((h, idx) => {
                                    const identifier = h.codigo || h.asignatura_nombre || `item-${idx}`;
                                    if (!subjectColorMap[identifier]) {
                                        subjectColorMap[identifier] = obtenerColorDistintivo(identifier, idx);
                                    }
                                });

                                const rowHeight = 42;
                                const gridTemplateColumns = `110px repeat(${totalLanes}, minmax(140px, 1fr))`;

                                return (
                                    <div className="bg-white rounded-lg shadow overflow-auto" style={{ maxHeight: '75vh' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns, position: 'relative' }}>
                                            <div className="border bg-gray-100 font-semibold text-center py-2 sticky top-0 z-20"
                                                style={{ gridColumn: '1', gridRow: '1' }}>
                                                Intervalo
                                            </div>

                                            {dayData.map((d, di) => (
                                                <div key={d.dia}
                                                    className="border bg-gray-100 font-bold text-center py-2 sticky top-0 z-20 uppercase"
                                                    style={{ gridColumn: `${dayOffsets[di] + 2} / span ${d.maxLanes}`, gridRow: '1' }}>
                                                    {d.dia}
                                                </div>
                                            ))}

                                            {Array.from({ length: totalSlots }).map((_, i) => {
                                                const mins = startHour * 60 + i * slotMinutes;
                                                const h = Math.floor(mins / 60), m = mins % 60;
                                                const mins2 = mins + slotMinutes;
                                                const hEnd = Math.floor(mins2 / 60), mEnd = mins2 % 60;
                                                const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} - ${String(hEnd).padStart(2, '0')}:${String(mEnd).padStart(2, '0')}`;
                                                return (
                                                    <div key={i} className="border bg-gray-50 text-xs flex items-center justify-center px-1 font-mono text-gray-500"
                                                        style={{ gridColumn: '1', gridRow: i + 2, height: rowHeight }}>
                                                        {label}
                                                    </div>
                                                );
                                            })}

                                            {dayData.map((d, di) => (
                                                Array.from({ length: totalSlots }).map((_, i) => (
                                                    Array.from({ length: d.maxLanes }).map((_, li) => (
                                                        <div key={`bg-${di}-${i}-${li}`} className="border bg-white"
                                                            style={{ gridColumn: dayOffsets[di] + 2 + li, gridRow: i + 2, height: rowHeight }} />
                                                    ))
                                                ))
                                            ))}

                                            {dayData.map((d, di) => {
                                                return d.entries.map((e, ei) => {
                                                    const startSlotIndex = Math.floor((e.startMin - (startHour * 60)) / slotMinutes);
                                                    const durationMinutes = e.endMin - e.startMin;
                                                    const spanRows = Math.max(1, Math.ceil(durationMinutes / slotMinutes));
                                                    const gridRowStart = startSlotIndex + 2;
                                                    const gridCol = dayOffsets[di] + 2 + e.lane;

                                                    const codigoMateria = e.codigo || '???';
                                                    const nombreMateria = e.asignatura_nombre || '';
                                                    const identifier = codigoMateria || `item-${ei}`;
                                                    const boxColor = subjectColorMap[identifier] || paletaColores[0];
                                                    const docenteNombreCompleto = e.docente_nombre || '-';

                                                    return (
                                                        <div
                                                            key={`ev-${di}-${ei}`}
                                                            className={`p-2 text-white text-xs shadow-md flex flex-col justify-between overflow-hidden rounded transition-all ${boxColor}`}
                                                            style={{
                                                                gridColumn: gridCol,
                                                                gridRow: `${gridRowStart} / span ${spanRows}`,
                                                                margin: '1px',
                                                                zIndex: 10
                                                            }}
                                                            title={`${codigoMateria} ${nombreMateria ? `- ${nombreMateria}` : ''}\nDocente: ${docenteNombreCompleto}\nSala: ${e.sala || '-'}\nParalelo: ${e.paralelo || '-'}\nHorario: ${e.hora_inicio?.substring(0, 5)} - ${e.hora_fin?.substring(0, 5)}`}
                                                        >
                                                            <div>
                                                                <div className="font-bold truncate">
                                                                    {codigoMateria} {e.paralelo ? `(${e.paralelo})` : ''}
                                                                </div>
                                                                {nombreMateria && nombreMateria !== codigoMateria && (
                                                                    <div className="truncate opacity-90 text-[11px]">{nombreMateria}</div>
                                                                )}
                                                            </div>
                                                            <div className="flex justify-between text-[10px] opacity-85 mt-1 border-t border-white/20 pt-0.5">
                                                                <span>Sala: {e.sala || '?'}</span>
                                                                <span className="truncate max-w-[80px]">{docenteNombreCompleto.split(' ')[0] || '-'}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Crear/Editar Asignatura */}
            {showNewAsigModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">
                                {editingSubject ? 'Editar Asignatura' : 'Nueva Asignatura'}
                            </h2>
                            <button onClick={() => setShowNewAsigModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <X size={28} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Código *</label>
                                <input
                                    type="text"
                                    value={newAsigData.codigo}
                                    onChange={(e) => setNewAsigData({ ...newAsigData, codigo: e.target.value })}
                                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md"
                                    placeholder="Ej: CPA-05"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Nombre *</label>
                                <input
                                    type="text"
                                    value={newAsigData.nombre}
                                    onChange={(e) => setNewAsigData({ ...newAsigData, nombre: e.target.value })}
                                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md"
                                    placeholder="Nombre de la asignatura"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Tipo</label>
                                    <select
                                        value={newAsigData.tipo}
                                        onChange={(e) => setNewAsigData({ ...newAsigData, tipo: e.target.value })}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md"
                                    >
                                        <option value="Práctica">Práctica</option>
                                        <option value="Teórica">Teórica</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Horas</label>
                                    <input
                                        type="number"
                                        value={newAsigData.horas}
                                        onChange={(e) => setNewAsigData({ ...newAsigData, horas: parseInt(e.target.value) || 4 })}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md"
                                        min="1"
                                    />
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">Modalidad: {selectedModalidad}</p>
                        </div>
                        <div className="px-6 py-4 border-t flex gap-3 justify-end">
                            <button
                                onClick={() => setShowNewAsigModal(false)}
                                className="px-6 py-2 border rounded-md hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveAsignatura}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                {editingSubject ? 'Actualizar' : 'Crear'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Confirmación Solicitar Aprobación */}
            {showApprovalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center border-b sticky top-0 bg-white z-10 mb-6">
                                <h2 className="text-xl font-bold text-blue-900">Solicitar Aprobación</h2>
                                <button onClick={() => setShowApprovalModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>
                            {/* <h2 className="text-xl font-bold text-blue-900">Solicitar Aprobación</h2> */}
                            <p className="text-gray-600 mb-6">
                                ¿Desea solicitar la aprobación de los horarios de la gestión <strong>{selectedGestion}</strong> a Dirección?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                Se enviarán todas las asignaturas de {selectedModalidad === 'CPA' ? 'Curso Preadmisión' : 'Prueba Admisión Académica'}.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowApprovalModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            const response = await fetch(`${API_URL}/jefatura/solicitar-aprobacion-gestion`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ gestion: selectedGestion, tipo: selectedModalidad })
                                            });
                                            const data = await response.json();
                                            if (response.ok) {
                                                alert(data.message);
                                                setShowApprovalModal(false);
                                            } else {
                                                alert(data.error || 'Error al solicitar aprobación');
                                            }
                                        } catch (error) {
                                            console.error('Error:', error);
                                            alert('Error al solicitar aprobación');
                                        }
                                    }}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Observaciones (Compartido CPA/PAA) */}
            {selectedObservation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold flex items-center gap-2 text-orange-600">
                                <MessageSquare size={20} />
                                Observaciones de Dirección
                            </h2>
                            <button onClick={() => setSelectedObservation(null)} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <X size={28} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="bg-orange-50 text-orange-900 p-4 rounded-lg text-sm mb-6 border border-orange-100 max-h-[400px] overflow-y-auto whitespace-pre-wrap">
                                {selectedObservation}
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setSelectedObservation(null)}
                                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition text-sm font-medium"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
